import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import reportsService from '../services/reportsService';
import useStoreTimezone from '../hooks/useStoreTimezone';
import { formatInStoreTimezone, toApiDateValue } from '../utils/dateUtils';

const formatCurrency = (value = 0) => `$${Number(value || 0).toFixed(2)}`;

const defaultFilters = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6);
  return {
    startDate: toApiDateValue(start),
    endDate: toApiDateValue(end),
    tillId: '',
    status: 'closed',
    varianceSign: 'any',
    varianceMin: '',
    varianceMax: '',
    hasNotes: 'all',
    searchNote: '',
  };
};

const TillSessionReportPage = () => {
  const { user } = useAuth();
  const { activeStore } = useStore();
  const timezone = useStoreTimezone();
  const canViewReports = user?.permissions?.includes('reports:view');

  const [filters, setFilters] = useState(() => defaultFilters());
  const [tills, setTills] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!activeStore?._id) {
      setTills([]);
      return;
    }
    const fetchTills = async () => {
      try {
        const response = await reportsService.getTillSessionsReportTills();
        const sortedTills = [...(response?.data?.data || [])].sort((a, b) =>
          String(a?.name || '').localeCompare(String(b?.name || ''))
        );
        setTills(sortedTills);
      } catch (err) {
        console.warn('Unable to load tills for till session report', err);
        setTills([]);
      }
    };
    fetchTills();
  }, [activeStore?._id]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [activeStore?._id]);

  useEffect(() => {
    if (!canViewReports || !activeStore?._id) {
      setRows([]);
      setSummary(null);
      return;
    }

    const fetchReport = async () => {
      try {
        setLoading(true);
        setError('');

        const params = {
          ...filters,
          timezone,
          page: pagination.page,
          limit: pagination.limit,
          sortBy: 'closingTime',
          sortOrder: 'desc',
        };
        if (!params.tillId) delete params.tillId;
        if (!params.varianceMin) delete params.varianceMin;
        if (!params.varianceMax) delete params.varianceMax;
        if (!params.searchNote) delete params.searchNote;
        if (params.hasNotes === 'all') {
          delete params.hasNotes;
        } else {
          params.hasNotes = params.hasNotes === 'yes';
        }

        const response = await reportsService.getTillSessionsReport(params);
        setRows(response?.data?.data || []);
        setSummary(response?.data?.summary || null);
        setPagination((prev) => ({
          ...prev,
          ...(response?.data?.pagination || {}),
        }));
      } catch (err) {
        setError(err?.response?.data?.message || 'Unable to load till session report.');
        setRows([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [activeStore?._id, canViewReports, filters, pagination.limit, pagination.page, timezone]);

  const canGoPrev = pagination.page > 1;
  const canGoNext = pagination.page < (pagination.totalPages || 1);

  const summaryData = useMemo(
    () =>
      summary || {
        totalSessions: 0,
        closedSessions: 0,
        openSessions: 0,
        totalExpectedCash: 0,
        totalCountedCash: 0,
        totalVariance: 0,
        notesCount: 0,
      },
    [summary]
  );

  if (!canViewReports) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-gray-800">Till Session Report</h1>
        <p className="mt-4 text-gray-600">You do not have permission to view till session reports.</p>
      </div>
    );
  }

  if (!activeStore?._id) {
    return <div className="p-6 text-slate-600">Please select a store to view till session report.</div>;
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Till Session Report</h1>
        <p className="text-gray-500">Review till close variance and notes for {activeStore?.name || 'store'}.</p>
      </div>

      <div className="bg-white p-4 rounded-xl shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">From</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => {
                setPagination((prev) => ({ ...prev, page: 1 }));
                setFilters((prev) => ({ ...prev, startDate: e.target.value }));
              }}
              className="border rounded-md px-3 py-2 text-sm w-full"
              max={filters.endDate}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">To</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => {
                setPagination((prev) => ({ ...prev, page: 1 }));
                setFilters((prev) => ({ ...prev, endDate: e.target.value }));
              }}
              className="border rounded-md px-3 py-2 text-sm w-full"
              min={filters.startDate}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Till</label>
            <select
              value={filters.tillId}
              onChange={(e) => {
                setPagination((prev) => ({ ...prev, page: 1 }));
                setFilters((prev) => ({ ...prev, tillId: e.target.value }));
              }}
              className="border rounded-md px-3 py-2 text-sm w-full"
            >
              <option value="">All tills</option>
              {tills.map((till) => (
                <option key={till._id} value={till._id}>
                  {till.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setPagination((prev) => ({ ...prev, page: 1 }));
                setFilters((prev) => ({ ...prev, status: e.target.value }));
              }}
              className="border rounded-md px-3 py-2 text-sm w-full"
            >
              <option value="closed">Closed</option>
              <option value="open">Open</option>
              <option value="all">All</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Notes</label>
            <select
              value={filters.hasNotes}
              onChange={(e) => {
                setPagination((prev) => ({ ...prev, page: 1 }));
                setFilters((prev) => ({ ...prev, hasNotes: e.target.value }));
              }}
              className="border rounded-md px-3 py-2 text-sm w-full"
            >
              <option value="all">All</option>
              <option value="yes">Notes present</option>
              <option value="no">No notes</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Variance</label>
            <select
              value={filters.varianceSign}
              onChange={(e) => {
                setPagination((prev) => ({ ...prev, page: 1 }));
                setFilters((prev) => ({ ...prev, varianceSign: e.target.value }));
              }}
              className="border rounded-md px-3 py-2 text-sm w-full"
            >
              <option value="any">Any</option>
              <option value="short">Short (negative)</option>
              <option value="over">Over (positive)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Min variance</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={filters.varianceMin}
              onChange={(e) => {
                setPagination((prev) => ({ ...prev, page: 1 }));
                setFilters((prev) => ({ ...prev, varianceMin: e.target.value }));
              }}
              className="border rounded-md px-3 py-2 text-sm w-full"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Max variance</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={filters.varianceMax}
              onChange={(e) => {
                setPagination((prev) => ({ ...prev, page: 1 }));
                setFilters((prev) => ({ ...prev, varianceMax: e.target.value }));
              }}
              className="border rounded-md px-3 py-2 text-sm w-full"
              placeholder="0.00"
            />
          </div>
          <div className="xl:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Note text</label>
            <input
              type="text"
              value={filters.searchNote}
              onChange={(e) => {
                setPagination((prev) => ({ ...prev, page: 1 }));
                setFilters((prev) => ({ ...prev, searchNote: e.target.value }));
              }}
              className="border rounded-md px-3 py-2 text-sm w-full"
              placeholder="Search note content"
            />
          </div>
        </div>
      </div>

      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-xs uppercase text-gray-500">Sessions</p>
          <p className="text-2xl font-semibold">{summaryData.totalSessions}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-xs uppercase text-gray-500">Closed</p>
          <p className="text-2xl font-semibold">{summaryData.closedSessions}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-xs uppercase text-gray-500">Open</p>
          <p className="text-2xl font-semibold">{summaryData.openSessions}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-xs uppercase text-gray-500">Expected Cash</p>
          <p className="text-2xl font-semibold">{formatCurrency(summaryData.totalExpectedCash)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-xs uppercase text-gray-500">Counted Cash</p>
          <p className="text-2xl font-semibold">{formatCurrency(summaryData.totalCountedCash)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-xs uppercase text-gray-500">Variance</p>
          <p
            className={`text-2xl font-semibold ${
              Number(summaryData.totalVariance) < 0 ? 'text-red-600' : Number(summaryData.totalVariance) > 0 ? 'text-emerald-700' : 'text-gray-900'
            }`}
          >
            {formatCurrency(summaryData.totalVariance)}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-xs uppercase text-gray-500">Notes</p>
          <p className="text-2xl font-semibold">{summaryData.notesCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow p-4">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-gray-600">Till</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-600">Opened</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-600">Closed</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-600">Status</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-600">Expected</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-600">Counted</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-600">Variance</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-600">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center py-5 text-gray-500">
                    Loading till sessions...
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-5 text-gray-500">
                    No till sessions found for the selected filters.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((row) => (
                  <tr key={row._id}>
                    <td className="px-4 py-2 font-medium text-gray-900">{row.tillName || 'Unknown till'}</td>
                    <td className="px-4 py-2 text-gray-700">
                      {row.openingTime ? formatInStoreTimezone(row.openingTime, 'dd-MM-yyyy HH:mm', timezone) : '-'}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {row.closingTime ? formatInStoreTimezone(row.closingTime, 'dd-MM-yyyy HH:mm', timezone) : '-'}
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700">
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">{formatCurrency(row.expectedCash)}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(row.closingFloat)}</td>
                    <td
                      className={`px-4 py-2 text-right font-semibold ${
                        Number(row.cashVariance) < 0
                          ? 'text-red-600'
                          : Number(row.cashVariance) > 0
                            ? 'text-emerald-700'
                            : 'text-gray-800'
                      }`}
                    >
                      {formatCurrency(row.cashVariance)}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {row.hasNotes ? row.closingNote : <span className="text-gray-400">No note</span>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages || 1}
            {pagination.total ? ` · ${pagination.total} rows` : ''}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => canGoPrev && setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
              disabled={!canGoPrev}
              className="px-3 py-1.5 rounded border bg-white text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => canGoNext && setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
              disabled={!canGoNext}
              className="px-3 py-1.5 rounded border bg-white text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TillSessionReportPage;
